import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import { ACTIVITY_LABELS } from './activity';
import { STATE_LABELS } from './looks';
import { anticLabel } from './idleAntics';
import './bailey.css';
import { normaliseAppearance, appearanceKey, logoInk } from './appearance';

// Each cut, drawn flat: `behind` him (long lengths, which fall behind his
// shoulders), `back` of the head (a bob's, the afro's volume, a pony's tail)
// and `front`, over the head. `h` is the hair colour, `x` the second colour
// where the technique shows one (highlights, balayage ends, dipped tips).
const cap = 'M29 58Q26 24 58 22H102Q134 24 131 58Q128 44 118 38Q100 30 80 30Q60 30 42 38Q32 44 29 58Z';
const STILL_CUTS = {
    fade: { front: (h) => <path d="M29 52Q22 18 55 19H107Q139 20 131 54L121 39Q80 32 39 39Z" fill={h} /> },
    buzz: { front: (h) => <path d={cap} fill={h} opacity=".92" /> },
    quiff: {
        front: (h, x) => <g>
            <path d="M29 52Q22 18 55 19H107Q139 20 131 54L121 39Q80 32 39 39Z" fill={h} opacity=".9" />
            <path d="M36 38Q30 12 58 6Q92 -1 114 12Q128 22 124 36Q108 22 84 24Q58 26 36 38Z" fill={h} />
            <path d="M56 12Q84 4 108 16" stroke={x} strokeWidth="3" strokeLinecap="round" />
        </g>,
    },
    pixie: {
        front: (h, x) => <g>
            <path d="M28 64Q24 20 62 18H100Q136 20 132 62Q128 44 116 38Q98 30 80 30Q60 30 44 38Q32 46 28 64Z" fill={h} />
            <path d="M36 44Q58 16 104 24Q126 30 126 48Q112 38 96 41Q80 45 70 60Q64 47 50 47Q42 48 36 44Z" fill={h} />
            <path d="M58 30Q82 24 104 30" stroke={x} strokeWidth="3" strokeLinecap="round" />
        </g>,
    },
    curls: {
        front: (h, x) => Array.from({ length: 13 }, (_, i) => (
            <circle key={i} cx={30 + i * 8.4} cy={30 - Math.sin(i / 12 * Math.PI) * 11} r="11" fill={i % 3 ? h : x} />
        )),
    },
    afro: {
        back: (h, x) => <g>
            <ellipse cx="80" cy="52" rx="62" ry="47" fill={h} />
            {Array.from({ length: 17 }, (_, i) => {
                const a = Math.PI * (0.92 + i / 16 * 1.16);
                return <circle key={i} cx={80 + Math.cos(a) * 60} cy={52 + Math.sin(a) * 45} r="12" fill={i % 4 === 1 ? x : h} />;
            })}
        </g>,
        front: (h) => <path d="M29 54Q28 22 80 20Q132 22 131 54Q118 36 80 36Q42 36 29 54Z" fill={h} />,
    },
    bob: {
        back: (h) => <rect x="21" y="21" width="118" height="99" rx="29" fill={h} />,
        front: (h) => <path d="M25 102V50Q20 15 68 18H103Q140 15 136 61V106H124V47Q94 23 76 38Q50 48 36 44V104Z" fill={h} />,
    },
    sleek: {
        behind: (h, x) => <g>
            <path d="M22 60Q20 20 80 16Q140 20 138 60V150Q138 157 130 157H30Q22 157 22 150Z" fill={h} />
            <path d="M28 70V150M132 70V150" stroke={x} strokeWidth="3" strokeLinecap="round" />
        </g>,
        front: (h, x) => <g>
            <path d="M26 110V54Q24 20 80 18Q136 20 134 54V110H124V52Q122 34 84 26L80 24L76 26Q38 34 36 52V110Z" fill={h} />
            <path d="M46 36Q60 26 76 26" stroke={x} strokeWidth="2.5" strokeLinecap="round" />
        </g>,
    },
    waves: {
        behind: (h) => <path d="M22 60Q20 20 80 16Q140 20 138 60Q146 79 137 97Q146 116 137 134Q144 150 130 157H30Q16 150 23 134Q14 116 23 97Q14 79 22 60Z" fill={h} />,
        front: (h, x) => <g>
            <path d="M26 112V56Q24 20 96 18Q136 20 134 56V112H124V54Q122 34 98 26Q60 30 40 46Q36 50 36 56V112Z" fill={h} />
            <path d="M126 70Q132 84 126 98M34 70Q28 84 34 98" stroke={x} strokeWidth="2.5" strokeLinecap="round" />
        </g>,
    },
    pony: {
        back: (h) => <path d="M88 12Q126 2 132 34Q138 70 126 104" stroke={h} strokeWidth="17" strokeLinecap="round" />,
        front: (h, x) => <g>
            <ellipse cx="80" cy="11" rx="13" ry="9" fill={h} />
            <path d="M29 50Q26 20 62 17H98Q134 20 131 50Q124 36 104 32Q80 28 56 32Q36 36 29 50Z" fill={h} />
            <rect x="71" y="16" width="18" height="5" rx="2.5" fill="#29282d" />
            <path d="M58 24Q80 18 102 24" stroke={x} strokeWidth="2.5" strokeLinecap="round" />
        </g>,
    },
    braids: {
        behind: (h, x) => <g>
            {[21, 28, 35, 118, 125, 132].map((bx, i) => (
                <g key={bx}>
                    <rect x={bx - 3.5} y="56" width="7" height={92 - (i % 3) * 5} rx="3.5" fill={h} />
                    <rect x={bx - 4.5} y={128 - (i % 3) * 5} width="9" height="12" rx="3" fill={x} />
                    {i % 2 === 0 && <rect x={bx - 4.5} y={118 - (i % 3) * 5} width="9" height="4" rx="1.5" fill="#d1a54a" />}
                </g>
            ))}
        </g>,
        front: (h) => <g>
            <path d="M28 60Q26 20 80 18Q134 20 132 60Q128 42 110 36Q80 30 50 36Q32 42 28 60Z" fill={h} />
            <path d="M80 19V32M54 22Q52 30 50 36M106 22Q108 30 110 36M36 40Q80 26 124 40" stroke="#000" strokeOpacity=".28" strokeWidth="1.2" />
        </g>,
    },
};

// A deliberately lightweight, non-animated fallback. The style chair's cut
// tiles show it until their 3D pictures are taken (cutThumbs.js).
export function BaileyStill({ appearance, face = false }) {
    const a = normaliseAppearance(appearance);
    const cut = STILL_CUTS[a.hairStyle] || {};
    const h = a.hairColour;
    // The second colour, where the technique shows one
    const x = a.hairTechnique === 'solid' ? a.hairColour : a.hairAccent;
    const lips = a.makeupEnabled && a.lipsOn;
    // `face` crops to the head, for the small marker beside a reply.
    return (
        <svg viewBox={face ? '16 6 128 118' : '0 0 160 180'} fill="none" aria-hidden="true" focusable="false">
            <ellipse cx="80" cy="167" rx="42" ry="7" fill="#302a20" opacity=".08" />
            {cut.behind && cut.behind(h, x)}
            <rect x="57" y="148" width="22" height="18" rx="8" fill="#c9c1b5" />
            <rect x="83" y="148" width="22" height="18" rx="8" fill="#c9c1b5" />
            <path d="M53 110L41 132L53 139V153H107V139L119 132L107 110Z" fill={a.shirtColour} />
            <ellipse cx="47" cy="142" rx="7" ry="10" fill="#c9c1b5" />
            <ellipse cx="113" cy="142" rx="7" ry="10" fill="#c9c1b5" />
            <text x="91" y="129" fill={logoInk(a.shirtColour)} fontSize="8" fontFamily="Arial, sans-serif">beu</text>
            {cut.back && cut.back(h, x)}
            <rect x="27" y="24" width="106" height="92" rx="30" fill="#c9c1b5" />
            {cut.front && cut.front(h, x)}
            <ellipse cx="59" cy="72" rx="13" ry="16" fill="#fffaf1" />
            <ellipse cx="102" cy="72" rx="13" ry="16" fill="#fffaf1" />
            <ellipse cx="63" cy="73" rx="6" ry="10" fill="#242220" />
            <ellipse cx="106" cy="73" rx="6" ry="10" fill="#242220" />
            <path d="M50 51Q59 47 67 51M94 51Q102 47 110 51" stroke="#302820" strokeWidth="2.5" strokeLinecap="round" />
            {lips ? (
                // Lips along the smile, as in 3D: a bowed upper lip, a fuller
                // lower lip and a darker parting.
                <g fill={a.lipColour}>
                    <path d="M73.5 97Q77 95.8 80.5 98Q84 95.8 87.5 97Q80.5 102.6 73.5 97Z" />
                    <path d="M73.5 97Q80.5 102.6 87.5 97Q80.5 107.5 73.5 97Z" />
                    <path d="M74.5 97.4Q80.5 102.2 86.5 97.4" fill="none" stroke="#242220" strokeOpacity=".4" strokeWidth=".9" strokeLinecap="round" />
                </g>
            ) : <path d="M74 97Q80 103 87 97" stroke="#242220" strokeWidth="2" strokeLinecap="round" />}
            {a.makeupEnabled && a.linerOn && <path d="M46 68Q48 52 61 55M89 68Q91 52 104 55" stroke={a.linerColour} strokeWidth="3" strokeLinecap="round" />}
            {a.makeupEnabled && a.blushOn && <g fill={a.blushColour} opacity=".35"><ellipse cx="42" cy="90" rx="9" ry="5" /><ellipse cx="118" cy="90" rx="9" ry="5" /></g>}
            {a.nailsEnabled && !face && <g fill={a.nailColour}><ellipse cx="45" cy="149" rx="2.2" ry="2.6" /><ellipse cx="49" cy="150" rx="2.2" ry="2.6" /><ellipse cx="111" cy="150" rx="2.2" ry="2.6" /><ellipse cx="115" cy="149" rx="2.2" ry="2.6" /></g>}
        </svg>
    );
}

const BaileyAvatar = forwardRef(function BaileyAvatar({ appearance, state = 'idle', activity = 'none', paused = false, studio = false, shot = 'full', className = '' }, ref) {
    const canvasRef = useRef(null);
    const controller = useRef(null);
    const latest = useRef(null);
    const [loaded, setLoaded] = useState(false);
    const [failed, setFailed] = useState(false);
    const key = appearanceKey(appearance);
    latest.current = { appearance: normaliseAppearance(appearance), state, activity, paused, studio, shot };
    useImperativeHandle(ref, () => ({
        rotateBy: delta => controller.current && controller.current.rotateBy(delta),
        spin: () => controller.current && controller.current.spin(),
        resetRotation: () => controller.current && controller.current.resetRotation(),
        // The style chair: a reaction (reactions.js) and the save's reveal.
        react: (id, side) => controller.current && controller.current.react(id, side),
        reveal: phase => controller.current && controller.current.reveal(phase),
        get ready() { return !!controller.current; }
    }), []);

    useEffect(() => {
        if (failed) return undefined;
        let alive = true;
        // Loads only when an authorised assistant screen actually mounts.
        import(/* webpackChunkName: "bailey-avatar" */ './scene').then(({ mountBailey }) => {
            if (!alive) return;
            try {
                controller.current = mountBailey(canvasRef.current, {
                    ...latest.current,
                    onReady: () => { if (alive) setLoaded(true); },
                    onError: () => { if (alive) setFailed(true); }
                });
            } catch (e) { if (alive) setFailed(true); }
        }).catch(() => { if (alive) setFailed(true); });
        return () => {
            alive = false;
            if (controller.current) controller.current.dispose();
            controller.current = null;
        };
    }, [failed, studio]);

    useEffect(() => {
        if (controller.current) controller.current.update(latest.current);
    }, [key, state, activity, paused, shot]);

    return (
        <div className={`bailey-avatar ${studio ? 'is-studio' : ''} ${className}`} role="img" aria-label={`Bailey. ${(state === 'working' && ACTIVITY_LABELS[activity]) || STATE_LABELS[state] || anticLabel(state) || STATE_LABELS.idle}.`}>
            {/* A small Bailey shows the flat drawing while the 3D one loads. In
                the style chair the drawing is a different Bailey from the one
                about to appear, so the stage waits (a soft floor shadow) and
                the 3D Bailey fades in. */}
            {((!loaded && !studio) || failed) && <BaileyStill appearance={appearance} />}
            {!loaded && !failed && studio && <span className="bailey-stage-wait" aria-hidden="true" />}
            {failed && studio && <p className="bailey-3d-unavailable">3D preview unavailable on this device. You can still choose and save your style.</p>}
            {!failed && <canvas ref={canvasRef} className={loaded ? 'is-ready' : ''} aria-hidden="true" />}
        </div>
    );
});

export default BaileyAvatar;
